import { message } from "antd";
import { scenarioApi, type NodePayload } from "api/scenario.api";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Scenario, ScenarioNode, ScenarioNodeType } from "types/scenario";
import { NODE_KEY_ERROR, NODE_LINK_ERROR } from "views/Scenario/constants";
import {
  buildNewNodes,
  getChoiceLinkError,
  getDraftsInOrder,
  getPreferredNodeId,
  hasInvalidNodeKeys,
  insertNodes,
  isNewNode,
  relinkAfterEdit,
  removeLocalNode,
  resolveAddTarget,
  resolveBranchTarget,
} from "views/Scenario/utils";

interface UseScenarioNodeProps {
  scenario: Scenario;
}

const toNodePayload = (node: ScenarioNode): NodePayload => ({
  key: node.key,
  type: node.type,
  content: node.content,
  variableKey: node.variableKey,
  prevNodeKey: node.prevNodeKey,
  nextNodeKey: node.type === ScenarioNodeType.Choice ? "" : node.nextNodeKey,
  position: node.position,
  options:
    node.type === ScenarioNodeType.Choice
      ? (node.options ?? []).map((option, index) => ({
          label: option.label,
          nextNodeKey: option.nextNodeKey ?? "",
          position: option.position ?? index,
        }))
      : [],
});

const samePayload = (left: NodePayload, right: NodePayload) => JSON.stringify(left) === JSON.stringify(right);

export const useScenarioNode = ({ scenario }: UseScenarioNodeProps) => {
  const [detail, setDetail] = useState<Scenario>(scenario);
  const [nodes, setNodes] = useState<ScenarioNode[]>([]);
  const [startNodeKey, setStartNodeKey] = useState(scenario.startNodeKey);
  const [selectedNodeId, setSelectedNodeId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const selectedNode = useMemo(
    () => nodes.find((node) => node.id === selectedNodeId) ?? null,
    [nodes, selectedNodeId],
  );

  const applyDetail = useCallback((fresh: Scenario, selectKey?: string) => {
    const nextNodes = fresh.nodes ?? [];

    setDetail(fresh);
    setNodes(nextNodes);
    setStartNodeKey(fresh.startNodeKey);
    setSelectedNodeId((prev) => {
      const saved = selectKey ? nextNodes.find((node) => node.key === selectKey) : undefined;

      if (saved) {
        return saved.id;
      }

      return nextNodes.some((node) => node.id === prev)
        ? prev
        : getPreferredNodeId(nextNodes, fresh.startNodeKey);
    });
  }, []);

  const reloadNodes = useCallback(
    async (selectKey?: string) => {
      const { data } = await scenarioApi.findOne(scenario.id);

      applyDetail(data, selectKey);
    },
    [applyDetail, scenario.id],
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);

      try {
        const { data } = await scenarioApi.findOne(scenario.id);

        if (cancelled) {
          return;
        }

        applyDetail(data);
      } catch (error) {
        console.error(error);

        if (!cancelled) {
          setNodes([]);
          setSelectedNodeId(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [applyDetail, scenario.id]);

  const selectNode = useCallback((id: number) => {
    setSelectedNodeId(id);
  }, []);

  const applyCreated = useCallback(
    (created: ScenarioNode[], nextNodes: ScenarioNode[]) => {
      setNodes(nextNodes);

      if (!startNodeKey && created[0]) {
        setStartNodeKey(created[0].key);
      }

      if (created[0]) {
        setSelectedNodeId(created[0].id);
      }
    },
    [startNodeKey],
  );

  const addNode = useCallback(
    (type: ScenarioNodeType) => {
      const fromKey = selectedNode?.key ?? startNodeKey;
      const target = resolveAddTarget(nodes, fromKey);
      const created = buildNewNodes(nodes, detail, type, target);

      applyCreated(created, insertNodes(nodes, created, target));
    },
    [applyCreated, detail, nodes, selectedNode, startNodeKey],
  );

  const addBranchNode = useCallback(
    (choiceKey: string, optionIndex: number, type: ScenarioNodeType) => {
      const target = resolveBranchTarget(nodes, choiceKey, optionIndex);
      const created = buildNewNodes(nodes, detail, type, target);

      applyCreated(created, insertNodes(nodes, created, target));
    },
    [applyCreated, detail, nodes],
  );

  const editNode = useCallback((next: ScenarioNode) => {
    setNodes((prev) => {
      const current = prev.find((item) => item.id === next.id);

      if (current && current.key !== next.key) {
        setStartNodeKey((start) => (start === current.key ? next.key : start));
      }

      return relinkAfterEdit(prev, next);
    });
  }, []);

  const cancelNode = useCallback(
    (node: ScenarioNode) => {
      const origin = detail.nodes?.find((item) => item.id === node.id);

      if (!origin) {
        const remaining = removeLocalNode(nodes, detail.nodes, node);
        const nextStartKey = startNodeKey === node.key ? node.nextNodeKey || detail.startNodeKey : startNodeKey;

        setNodes(remaining);

        if (nextStartKey !== startNodeKey) {
          setStartNodeKey(nextStartKey);
        }

        setSelectedNodeId(getPreferredNodeId(remaining, nextStartKey));
        return;
      }

      setNodes((prev) => relinkAfterEdit(prev, origin));

      if (startNodeKey === node.key && origin.key !== node.key) {
        setStartNodeKey(origin.key);
      }
    },
    [detail.nodes, detail.startNodeKey, nodes, startNodeKey],
  );

  const saveNode = useCallback(
    async (node: ScenarioNode) => {
      if (saving) {
        return;
      }

      if (hasInvalidNodeKeys(nodes)) {
        message.warning(NODE_KEY_ERROR);
        return;
      }

      const linkError = nodes.map((item) => getChoiceLinkError(nodes, item)).find(Boolean);

      if (linkError) {
        message.warning(NODE_LINK_ERROR[linkError]);
        return;
      }

      setSaving(true);

      let didWrite = false;

      try {
        const drafts = getDraftsInOrder(nodes, startNodeKey);

        for (const draft of drafts) {
          await scenarioApi.createNode(scenario.id, toNodePayload(draft));
          didWrite = true;
        }

        for (const current of nodes) {
          if (isNewNode(current)) {
            continue;
          }

          const origin = detail.nodes?.find((item) => item.id === current.id);

          if (!origin || samePayload(toNodePayload(current), toNodePayload(origin))) {
            continue;
          }

          await scenarioApi.updateNode(scenario.id, current.id, toNodePayload(current));
          didWrite = true;
        }

        if (startNodeKey !== detail.startNodeKey) {
          await scenarioApi.update(scenario.id, {
            name: detail.name,
            startNodeKey,
          });
          didWrite = true;
        }

        message.success(isNewNode(node) ? "Tạo thành công!" : "Đã lưu thay đổi của node");
        await reloadNodes(node.key);
      } catch (error) {
        console.error(error);

        if (didWrite) {
          try {
            await reloadNodes(node.key);
          } catch (reloadError) {
            console.error(reloadError);
          }
        }
      } finally {
        setSaving(false);
      }
    },
    [detail.name, detail.nodes, detail.startNodeKey, nodes, reloadNodes, saving, scenario.id, startNodeKey],
  );

  const deleteNode = useCallback(
    async (node: ScenarioNode) => {
      if (isNewNode(node)) {
        const remaining = removeLocalNode(nodes, detail.nodes, node);
        const nextStartKey = startNodeKey === node.key ? node.nextNodeKey || detail.startNodeKey : startNodeKey;

        setNodes(remaining);

        if (nextStartKey !== startNodeKey) {
          setStartNodeKey(nextStartKey);
        }

        setSelectedNodeId(getPreferredNodeId(remaining, node.nextNodeKey || nextStartKey));
        return;
      }

      try {
        await scenarioApi.deleteNode(scenario.id, node.id);
        message.success("Xóa thành công");
        await reloadNodes();
      } catch (error) {
        console.error(error);
      }
    },
    [detail.nodes, detail.startNodeKey, nodes, reloadNodes, scenario.id, startNodeKey],
  );

  return {
    nodes,
    selectedNode,
    selectedNodeId,
    loadingNode: loading,
    savingNode: saving,
    startNodeKey,
    selectNode,
    addNode,
    addBranchNode,
    editNode,
    cancelNode,
    saveNode,
    deleteNode,
  };
};
